import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Footprints, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Panel izquierdo */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <Footprints size={22} />
          </div>
          <span className="font-head font-extrabold text-lg tracking-tight">PLM</span>
        </div>
        <div>
          <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
            El desarrollo de tu calzado, <span className="text-blue-400">en un solo lugar</span>.
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Centraliza fichas, imágenes y costos. Sigue las 24 compuertas de cada modelo en tiempo
            real y detecta dónde se atoran tus desarrollos. Adiós al Excel duplicado.
          </p>
        </div>
        <div className="text-slate-500 text-sm">Product Lifecycle Management · Calzado</div>
      </div>

      {/* Formulario */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center text-white">
              <Footprints size={22} />
            </div>
            <span className="font-head font-extrabold text-lg tracking-tight">PLM</span>
          </div>
          <h2 className="font-head text-2xl font-bold tracking-tight">Iniciar sesión</h2>
          <p className="text-sm text-slate-500 mt-1">Ingresa con tu cuenta para continuar.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Correo</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@aretina.com.mx"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full rounded-lg h-11" disabled={busy} data-testid="login-submit-button">
              {busy ? "Entrando…" : "Entrar"} <ArrowRight size={16} className="ml-1" />
            </Button>
          </form>

          <div className="mt-8">
            <p className="text-xs text-slate-400 text-center">
              ¿No tienes acceso? Contacta a tu Administrador.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
